import { NgClass } from "@angular/common";
import {
    Component,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule, ReactiveFormsModule, UntypedFormGroup } from "@angular/forms";
import { CardComponent } from "@components/cards/card/card.component";
import { InputComponent } from "@components/input/input.component";
import { SelectorComponent } from "@components/selector/selector.component";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { FormBinderService } from "~app/common/services/form-binder.service";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { IValidationScope } from "~app/common/validation/IValidationScope";
import { ValidationContext } from "~app/common/validation/validationContext";
import { ValidationScopes } from "~app/common/validation/ValidationScopes";
import { UpdateAtgardAvslutaArbetsorderGrotCommand } from "~app/features/planera/commands/UpdateAtgardAvslutaArbetsorderGrotCommand";
import { IArbetsorderDetail } from "~app/features/planera/model/IArbetsorderDetail";
import { IAtgard } from "~app/features/planera/model/IAtgard";
import { ArbetsorderMediatorService } from "~app/features/planera/services/arbetsorder-mediator.service";
import { ArbetsorderAtgardService } from "../../arbetsorder-atgard.service";
import { ArbetsorderAtgardProduktionAvslutadGrotValidation } from "./arbetsorder-atgard-produktion-avslutad-grot.validation";

const GROT_LAMNAD = "Grot lämnat på hygget";
const SKOTAS_SENARE = "Skotas senare";
const UTFORD = "Utförd och skotad";

@Component({
    selector: "rask-arbetsorder-atgard-produktion-avslutad-grot",
    templateUrl: "./arbetsorder-atgard-produktion-avslutad-grot.component.html",
    styleUrls: ["./arbetsorder-atgard-produktion-avslutad-grot.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        CardComponent,
        NgClass,
        InputComponent,
        SelectorComponent
    ]
})
export class ArbetsorderAtgardProduktionAvslutadGrotComponent implements OnInit, OnDestroy, OnChanges  {
    readonly atgardService = inject(ArbetsorderAtgardService);

    @Input() arbetsorder: IArbetsorderDetail;
    @Input() atgard: IAtgard;
    form: UntypedFormGroup;
    @Input() rootForm: UntypedFormGroup;
    statusValues = [
        UTFORD,
        GROT_LAMNAD,
        SKOTAS_SENARE
    ];
    validation: IValidationScope;
    validationContext: ValidationContext;
    viewModel: { currentStatus: string } = { currentStatus: undefined };

    private readonly formBinder = inject(FormBinderService);
    private readonly arbetsorderMediatorService = inject(ArbetsorderMediatorService);
    private readonly unsubscribe$: Subject<void>;

    constructor() {
        this.unsubscribe$ = new Subject<void>();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.arbetsorder && this.validationContext) {
            this.setViewModelValue();
            this.validationContext.validate();
        }
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    ngOnInit(): void {
        this.setUpForm();
    }

    private addUpdateCommand(): void {
        this.arbetsorder.commandManager.addOrUpdate(new UpdateAtgardAvslutaArbetsorderGrotCommand(c => {
            c.atgardId = this.atgard.id;
            c.atgardUtford = this.atgard.atgardUtford;
            c.grotLamnatPaHygge = this.atgard.grotLamnatPaHygge;
        }), command => command.atgardId === this.atgard.id);
    }

    private setModelValue(): void {
        switch (this.viewModel.currentStatus) {
            case UTFORD:
                this.atgard.atgardUtford = true;
                this.atgard.grotLamnatPaHygge = false;
                break;
            case GROT_LAMNAD:
                this.atgard.atgardUtford = true;
                this.atgard.grotLamnatPaHygge = true;
                break;
            case SKOTAS_SENARE:
                this.atgard.atgardUtford = false;
                this.atgard.grotLamnatPaHygge = null;
                break;
            default:
                this.atgard.atgardUtford = null;
                this.atgard.grotLamnatPaHygge = null;
                break;
        }
    }

    private setUpForm(): void {
        this.setViewModelValue();
        this.form = this.formBinder.CreateGroup(this.viewModel);
        this.validationContext = ArbetsorderAtgardProduktionAvslutadGrotValidation.init(this.viewModel);
        this.arbetsorderMediatorService.addValidationContext(this.validationContext);
        this.validationContext.scope(ValidationScopes.spara).subscribe(v => {
            this.validation = v;
        });

        this.form.valueChanges
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(x => {
                this.setModelValue();
                this.addUpdateCommand();
            });

        this.formBinder.attachToRootForm(this.rootForm, this.form, `atgard_grot_${this.atgard.id}`).subscribe(x => {
            this.formBinder.attachValidation(this.validationContext.current, this.form, true);
            this.validationContext.validate();
        });
    }

    private setViewModelValue(): void {
        if (!!this.atgard.atgardUtford && this.atgard.grotLamnatPaHygge === false) {
            this.viewModel.currentStatus = UTFORD;
            return;
        }

        if (!!this.atgard.atgardUtford && !!this.atgard.grotLamnatPaHygge) {
            this.viewModel.currentStatus = GROT_LAMNAD;
            return;
        }

        if (this.atgard.atgardUtford === false && ObjectUtils.isNullOrUndefined(this.atgard.grotLamnatPaHygge)) {
            this.viewModel.currentStatus = SKOTAS_SENARE;
            return;
        }

        this.viewModel.currentStatus = undefined;
    }
}
